import { currencyTable } from "./currency-table.ts";

/** Derived from the table, never restated — the shape cannot drift from the data. */
export type CurrencyEntry = (typeof currencyTable)[keyof typeof currencyTable];

/** The `as const` table infers literal keys, so a lookup by a plain string needs
 *  the table widened. Doing it here once is what keeps the cast out of call sites. */
const CURRENCIES: Readonly<Record<string, CurrencyEntry | undefined>> =
  currencyTable;

/** Shown when a money field names no currency, or names one not in the table. */
export const DEFAULT_CURRENCY_SYMBOL = "$";

/** Every key in the table is lowercase, and a stored code is not normalized.
 *  Lowercasing here is what makes a stored `"USD"` resolve — without it the
 *  money format check silently falls back to "accept anything". */
export function currency(
  code: string | null | undefined,
): CurrencyEntry | undefined {
  return code ? CURRENCIES[code.toLowerCase()] : undefined;
}

export function currencySymbol(code: string | null | undefined): string {
  return currency(code)?.symbol ?? DEFAULT_CURRENCY_SYMBOL;
}

/** Used when a money field names no currency, or names one not in the table —
 *  the same fallback as the symbol, so an unconfigured field behaves as USD. */
const DEFAULT_CURRENCY_FRACTION_DIGITS = 2;
const DEFAULT_CURRENCY_LOCALE = "en-US";

/** Minor-unit digits an amount in this currency may carry (0 for JPY/KRW). */
export function currencyFractionDigits(code: string | null | undefined): number {
  return currency(code)?.fractionDigits ?? DEFAULT_CURRENCY_FRACTION_DIGITS;
}

/** The locale whose group and decimal separators the amount is written in. */
export function currencyLocale(code: string | null | undefined): string {
  return currency(code)?.config.locale ?? DEFAULT_CURRENCY_LOCALE;
}

/** How many decimals `value` carries, read off its decimal string so float
 *  noise (`3.3 * 100 = 330.00000000000006`) cannot inflate the count. */
export function fractionDigitsOf(value: number): number {
  const text = value.toString();
  const exponent = /e-(\d+)$/i.exec(text);
  if (exponent) {
    const mantissaDigits = text.split("e")[0]!.split(".")[1]?.length ?? 0;
    return mantissaDigits + Number(exponent[1]);
  }
  return text.split(".")[1]?.length ?? 0;
}

/** The rest-state display of a money amount: the currency's symbol as a fixed
 *  prefix, then the number grouped in the currency's locale with only the
 *  decimals it actually carries, capped at the currency's minor unit —
 *  `$322`, `$3.3`, `$322.01`, `$-32,233.01`. The sign follows the symbol
 *  because the symbol is an adornment on the number, not part of it; the
 *  editor shows the same shape while typing. */
export function formatMoney(value: number, code: string | null | undefined): string {
  const number = new Intl.NumberFormat(currencyLocale(code), {
    maximumFractionDigits: currencyFractionDigits(code),
  }).format(value);
  return `${currencySymbol(code)}${number}`;
}

/** Picker options, in the table's declaration order. */
export const CURRENCY_OPTIONS: readonly { value: string; label: string }[] =
  Object.entries(CURRENCIES).map(([value, entry]) => ({
    value,
    label: entry!.label,
  }));
